import { useState, useMemo } from 'react';
import {
  Box,
  Chip,
  IconButton,
  TextField,
  Autocomplete,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { usePatient } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { PatientWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { produce } from 'immer';
import { Coding } from 'src/careflow/ph-core/fhir-client/resources/resources';
import { useTags } from 'src/careflow/care-coordination/tags/hooks/useTags';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import CustomModal from 'src/components/CustomModal';

type PatientTagsProps = {
  patient: WrappedPatient;
  onRefresh?: () => void;
  readOnly?: boolean;
};

export default function PatientTags({ patient, onRefresh, readOnly = false }: PatientTagsProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [selectedTag, setSelectedTag] = useState<{ code: string; display: string; system: string } | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [, { update, refresh: refreshPatient }] = usePatient(patient?.id, {
    autofetch: false,
    map: PatientWrapper,
  });

  const { data: allTags, isLoading: isLoadingTags } = useTags();

  const patientValueSets = useMemo(() => {
    if (!allTags) return [];
    
    return allTags.filter((valueSet: WrappedValueSet) => {
      const resourceTypeTag = valueSet.meta?.tag?.find(
        (tag) => tag.system === 'http://health.us/fhir/CodeSystem/resource-types' && tag.code === 'Patient'
      );
      const hasPatientInName = valueSet.name?.toLowerCase().includes('patient');
      return !!resourceTypeTag || hasPatientInName;
    });
  }, [allTags]);

  const tagOptions = useMemo(() => {
    if (!patientValueSets || patientValueSets.length === 0) return [];

    const allOptions: { code: string; display: string; system: string }[] = [];
    
    patientValueSets.forEach((valueSet: WrappedValueSet) => {
      try {
        if (valueSet.asListAll && typeof valueSet.asListAll === 'function') {
          const concepts = valueSet.asListAll();
          concepts.forEach((concept: any) => {
            if (concept.code) {
              allOptions.push({
                code: concept.code,
                display: concept.display || concept.code,
                system: concept.system || '',
              });
            }
          });
        } else {
          if (valueSet.compose?.include && Array.isArray(valueSet.compose.include)) {
            valueSet.compose.include.forEach((include) => {
              if (include.concept && Array.isArray(include.concept)) {
                const system = include.system || '';
                include.concept.forEach((concept) => {
                  if (concept.code) {
                    allOptions.push({
                      code: concept.code,
                      display: concept.display || concept.code,
                      system: system,
                    });
                  }
                });
              }
            });
          }
        }
      } catch (error) {
        // Silently handle errors
      }
    });

    const uniqueOptions = Array.from(
      new Map(
        allOptions.map((option) => [`${option.system}|${option.code}`, option])
      ).values()
    );

    return uniqueOptions;
  }, [patientValueSets]);

  const patientTags = useMemo(() => {
    if (!patient?.meta?.tag) return [];
    
    return patient.meta.tag
      .map((tag) => {
        const matchingOption = tagOptions.find(
          (option) => {
            if (option.code !== tag.code) return false;
            const optionSystemId = option.system?.split('/').pop();
            const tagSystemId = tag.system?.split('/').pop();
            return optionSystemId === tagSystemId || option.system === tag.system;
          }
        );
        
        return {
          ...tag,
          display: matchingOption?.display || tag.display || tag.code || 'Unknown Tag',
        };
      })
      .filter((tag) => {
        return tagOptions.some(
          (option) => {
            if (option.code !== tag.code) return false;
            const optionSystemId = option.system?.split('/').pop();
            const tagSystemId = tag.system?.split('/').pop();
            return optionSystemId === tagSystemId || option.system === tag.system;
          }
        );
      });
  }, [patient, tagOptions]);

  const isLoadingValueSets = isLoadingTags;

  const filteredOptions = useMemo(() => {
    if (!searchInput) return tagOptions;
    const searchLower = searchInput.toLowerCase();
    return tagOptions.filter(
      (option) =>
        option.display?.toLowerCase().includes(searchLower) ||
        option.code?.toLowerCase().includes(searchLower)
    );
  }, [tagOptions, searchInput]);

  const handleAddTag = async () => {
    if (!patient || !selectedTag) return;

    setIsUpdating(true);
    try {
      const updatedPatient = produce(patient, (draft) => {
        if (!draft.meta) {
          draft.meta = {};
        }
        if (!draft.meta.tag) {
          draft.meta.tag = [];
        }

        const tagExists = draft.meta.tag.some(
          (t) => t.system === selectedTag.system && t.code === selectedTag.code
        );

        if (!tagExists) {
          draft.meta.tag.push({
            system: selectedTag.system,
            code: selectedTag.code,
            display: selectedTag.display || selectedTag.code || 'Unknown Tag',
          });
        }
      });

      await update(updatedPatient);
      enqueueSnackbar('Tag added successfully', { variant: 'success' });
      setOpen(false);
      setSearchInput('');
      setSelectedTag(null);
      await refreshPatient();
      if (onRefresh) onRefresh();
    } catch (error) {
      enqueueSnackbar('Failed to add tag', { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCloseDialog = () => {
    if (!isUpdating) {
      setOpen(false);
      setSearchInput('');
      setSelectedTag(null);
    }
  };

  const handleRemoveTag = async (tagToRemove: Coding) => {
    if (!patient) return;

    setIsUpdating(true);
    try {
      const updatedPatient = produce(patient, (draft) => {
        if (!draft.meta) {
          draft.meta = {};
        }
        
        if (!draft.meta.tag) {
          draft.meta.tag = [];
        }
        
        if (Array.isArray(draft.meta.tag)) {
          const removeSystemId = tagToRemove.system?.split('/').pop() || '';
          const removeCode = tagToRemove.code;
          
          draft.meta.tag = draft.meta.tag.filter((t) => {
            if (t.code !== removeCode) {
              return true;
            }
            
            const tagSystemId = t.system?.split('/').pop() || '';
            const systemsMatch = 
              tagSystemId === removeSystemId || 
              t.system === tagToRemove.system;
            
            return !systemsMatch;
          });
          
          if (draft.meta.tag.length === 0) {
            draft.meta.tag = [];
          }
        } else {
          draft.meta.tag = [];
        }
      });

      await update(updatedPatient);
      enqueueSnackbar('Tag removed successfully', { variant: 'success' });
      await refreshPatient();
      if (onRefresh) onRefresh();
    } catch (error) {
      enqueueSnackbar('Failed to remove tag', { variant: 'error' });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 'medium' }}>
          Tags:
        </Typography>
        {patientTags.length > 0 ? (
          patientTags.map((tag, index) => {
            const originalTag = patient?.meta?.tag?.find((t) => {
              if (t.code !== tag.code) return false;
              const tagSystemId = t.system?.split('/').pop();
              const displaySystemId = tag.system?.split('/').pop();
              return tagSystemId === displaySystemId || t.system === tag.system;
            });
            
            return (
              <Chip
                key={`${tag.system}|${tag.code}-${index}`}
                label={tag.display || tag.code || 'Unknown Tag'}
                size="small"
                onDelete={readOnly ? undefined : () => handleRemoveTag(originalTag || tag)}
                deleteIcon={readOnly ? undefined : <DeleteIcon fontSize="small" />}
                sx={{
                  backgroundColor: '#E3F2FD',
                  color: '#1976D2',
                  fontWeight: 500,
                  '& .MuiChip-deleteIcon': {
                    color: '#1976D2',
                    fontSize: '16px',
                    opacity: 0.8,
                    '&:hover': {
                      color: '#d32f2f',
                      opacity: 1,
                    },
                  },
                }}
              />
            );
          })
        ) : (
          !readOnly && (
            <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
              No tags assigned
            </Typography>
          )
        )}
        {!readOnly && (
          <IconButton
            size="small"
            onClick={() => setOpen(true)}
            sx={{
              color: 'success.main',
              '&:hover': {
                backgroundColor: 'success.light',
                color: 'success.dark',
              },
            }}
            disabled={isUpdating}
          >
            <AddIcon />
          </IconButton>
        )}
      </Stack>

      <CustomModal
        open={open}
        onClose={handleCloseDialog}
        title="Add New Tag"
        breadcrumbs={[
          'Home',
          'CRS',
          patient?.getFullName() || 'Patient',
          'Tags',
        ]}
        onSave={handleAddTag}
        onCancel={handleCloseDialog}
        isLoading={isUpdating}
        saveButtonTitle="Add"
        isSaveButtonDisabled={!selectedTag || isUpdating}
        containerSx={{
          width: '40vw',
          maxWidth: '600px',
        }}
      >
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isLoadingValueSets && tagOptions.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No tags found. Tags will appear here once they are available in the valueset.
            </Typography>
          )}
          <Autocomplete
            options={filteredOptions}
            getOptionLabel={(option) => option.display || option.code || ''}
            loading={isLoadingValueSets}
            value={selectedTag}
            inputValue={searchInput}
            onInputChange={(_, newInputValue) => setSearchInput(newInputValue)}
            onChange={(_, value) => {
              setSelectedTag(value);
            }}
            disabled={tagOptions.length === 0 && !isLoadingValueSets}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tag"
                placeholder={
                  tagOptions.length === 0 && !isLoadingValueSets
                    ? 'No tags available'
                    : 'Type to search tags...'
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isLoadingValueSets ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Typography variant="body2">{option.display || option.code}</Typography>
              </Box>
            )}
            noOptionsText={
              isLoadingValueSets
                ? 'Loading tags...'
                : tagOptions.length === 0
                  ? 'No tags available'
                  : searchInput
                    ? 'No tags found matching your search'
                    : 'Start typing to search tags'
            }
          />
        </Stack>
      </CustomModal>
    </>
  );
}

